import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { signUp } from "../api/auth"; // Adjust the import path as needed
import { showAlert } from "../components/CustomAlert"; // Import showAlert
import { useNavigate } from "react-router-dom";

const SignupSchema = Yup.object().shape({
    firstname: Yup.string().required("First Name is required"),
    lastname: Yup.string().required("Last Name is required"),
    username: Yup.string().required("Username is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
        .min(6, "Password too short")
        .required("Password is required"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
    terms: Yup.bool().oneOf([true], "You must accept the terms and conditions"),
});

const SignupForm = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const response = await signUp(values);
            //console.log(response);
            showAlert(response.message); // Show success message
            if(response.status === "success"){
                navigate("/sign_in");
                resetForm();
            }
            //resetForm();
        } catch (error) {
            setSubmitting(false);
            showAlert("An error occurred. Please try again."); // Show error message
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-200">
            <div className="w-full max-w-md bg-blue-600 text-white p-6 rounded-lg shadow-lg">
                <h1 className="text-2xl font-bold text-center mb-2">Register</h1>
                <p className="text-center text-white mb-6">Create New Account</p>

                <Formik
                    initialValues={{
                        firstname: "",
                        lastname: "",
                        username: "",
                        email: "",
                        password: "",
                        confirmPassword: "",
                        terms: false,
                    }}
                    validationSchema={SignupSchema}
                    onSubmit={handleSubmit}
                >
                {({ isSubmitting }) => (
                    <Form>
                        {/* First Name and Last Name */}
                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <Field
                                    type="text"
                                    name="firstname"
                                    placeholder="First name"
                                    className="w-full p-3 text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <ErrorMessage
                                    name="firstname"
                                    component="div"
                                    className="text-sm text-red-500 mt-1"
                                />
                            </div>
                            <div className="flex-1">
                                <Field
                                    type="text"
                                    name="lastname"
                                    placeholder="Last name"
                                    className="w-full p-3 text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <ErrorMessage
                                    name="lastname"
                                    component="div"
                                    className="text-sm text-red-500 mt-1"
                                />
                            </div>
                        </div>

                        {/* Username */}
                        <div className="mb-4">
                            <Field
                                type="text"
                                name="username"
                                placeholder="Username"
                                className="w-full p-3 text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <ErrorMessage
                                name="username"
                                component="div"
                                className="text-sm text-red-500 mt-1"
                            />
                        </div>

                        {/* Email */}
                        <div className="mb-4">
                            <Field
                                type="email"
                                name="email"
                                placeholder="Enter Email"
                                className="w-full p-3 text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <ErrorMessage
                                name="email"
                                component="div"
                                className="text-sm text-red-500 mt-1"
                            />
                        </div>

                        {/* Password */}
                        <div className="relative mb-4">
                            <Field
                                type={showPassword ? "text" : "password"}
                                name="password"
                                placeholder="Password"
                                className="w-full p-3 text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <div
                                className="absolute right-3 top-3 cursor-pointer text-gray-700"
                                onClick={() => setShowPassword((prev) => !prev)}
                            >
                                {showPassword ? <FiEyeOff /> : <FiEye />}
                            </div>
                            <ErrorMessage
                                name="password"
                                component="div"
                                className="text-sm text-red-500 mt-1"
                            />
                        </div>

                        {/* Confirm Password */}
                        <div className="relative mb-4">
                            <Field
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                placeholder="Confirm Password"
                                className="w-full p-3 text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <div
                                className="absolute right-3 top-3 cursor-pointer text-gray-700"
                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                            >
                                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                            </div>
                            <ErrorMessage
                                name="confirmPassword"
                                component="div"
                                className="text-sm text-red-500 mt-1"
                            />
                        </div>

                        {/* Terms & Conditions */}
                        <div className="flex items-center mb-4">
                            <Field
                                type="checkbox"
                                name="terms"
                                className="w-4 h-4 text-blue-600 bg-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                            />
                            <label className="ml-2 text-sm text-white">
                                I Agree to{" "}
                                <span className="font-semibold underline">
                                    <Link
                                            to="/terms_and_conditions"
                                            className="font-semibold underline hover:text-gray-300"
                                            >                
                                            Terms & Conditions
                                    </Link>
                                </span>
                            </label>
                            <ErrorMessage
                                name="terms"
                                component="div"
                                className="text-sm text-red-500 ml-2"
                            />
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full py-3 rounded-lg text-white font-semibold transition duration-300 ${
                                isSubmitting
                                    ? "bg-blue-700 cursor-not-allowed"
                                    : "bg-blue-400 hover:bg-blue-700"
                            }`}
                        >
                            {isSubmitting ? "Submitting..." : "Create New Account"}
                        </button>

                        {/* Sign In Link */}
                        <p className="mt-4 text-center text-sm text-white">
                            Already have an account?{" "}
                            <Link
                                to="/sign_in"
                                className="font-semibold underline hover:text-gray-300"
                            >                
                                Sign in Here
                            </Link>
                        </p>
                    </Form>
                )}
                </Formik>
            </div>
        </div>
    );
};

export default SignupForm;
